import { useEffect, useRef, useState, useCallback } from 'react';
import * as PIXI from 'pixi.js';

// ============================================
// SCORECARD STYLING DEMO PAGE
// ============================================
// Allows visual configuration of scorecard appearance
// with live preview and copy-to-clipboard config export

// Fixed dimensions matching actual game scorecard (PixiGameTableV1.tsx line 8725)
// The card is 80x112, but rotated 90° in game, so visually it's 112 wide x 80 tall
const CARD_WIDTH = 112;  // Visual width (rotated height)
const CARD_HEIGHT = 80;  // Visual height (rotated width)
const CORNER_RADIUS = 6;

interface ScorecardConfig {
  // Colors
  backgroundColor: string;
  borderColor: string;
  headerBgColor: string;
  headerTextColor: string;
  dividerColor: string;
  rowTextColor: string;
  winColor: string;
  lossColor: string;
  pushColor: string;
  totalBgColor: string;
  totalTextColor: string;

  // Typography
  headerText: string;
  headerFontSize: number;
  rowFontSize: number;
  totalFontSize: number;
  fontFamily: string;

  // Spacing
  headerHeight: number;
  rowHeight: number;
  totalRowHeight: number;
  paddingX: number;
  paddingY: number;
  dividerThickness: number;
  borderThickness: number;

  // Alignment
  labelAlign: 'left' | 'center' | 'right';
  valueAlign: 'left' | 'center' | 'right';
}

const DEFAULT_CONFIG: ScorecardConfig = {
  backgroundColor: '#1F2937',
  borderColor: '#FFD700',
  headerBgColor: '#374151',
  headerTextColor: '#FFD700',
  dividerColor: '#4B5563',
  rowTextColor: '#E5E7EB',
  winColor: '#22C55E',
  lossColor: '#EF4444',
  pushColor: '#F59E0B',
  totalBgColor: '#111827',
  totalTextColor: '#FFD700',

  headerText: 'RESULTS',
  headerFontSize: 10,
  rowFontSize: 8,
  totalFontSize: 10,
  fontFamily: 'Arial',

  headerHeight: 16,
  rowHeight: 12,
  totalRowHeight: 16,
  paddingX: 6,
  paddingY: 3,
  dividerThickness: 1,
  borderThickness: 2,

  labelAlign: 'left',
  valueAlign: 'right',
};

interface DemoData {
  mainBet: { label: string; amount: number; outcome: 'win' | 'loss' | 'push' };
  sidebets: { label: string; amount: number; outcome: 'win' | 'loss' | 'push' }[];
  total: number;
}

// All possible sidebets for mixing (short labels to fit card)
const ALL_SIDEBETS = [
  { label: 'PP', amount: -25, outcome: 'loss' as const },
  { label: '21+3', amount: 75, outcome: 'win' as const },
  { label: 'Bust', amount: -15, outcome: 'loss' as const },
];

function generateScenario(
  outcomeType: 'win' | 'loss' | 'push' | 'mixed',
  sidebetCount: number
): DemoData {
  const sidebets = ALL_SIDEBETS.slice(0, sidebetCount);
  const sidebetTotal = sidebets.reduce((sum, sb) => sum + sb.amount, 0);

  switch (outcomeType) {
    case 'win':
      return {
        mainBet: { label: 'Main', amount: 50, outcome: 'win' },
        sidebets,
        total: 50 + sidebetTotal,
      };
    case 'loss':
      // Make sidebets all losses for loss scenario
      const lossSidebets = sidebets.map(sb => ({
        ...sb,
        amount: -Math.abs(sb.amount),
        outcome: 'loss' as const,
      }));
      const lossTotal = lossSidebets.reduce((sum, sb) => sum + sb.amount, 0);
      return {
        mainBet: { label: 'Main', amount: -100, outcome: 'loss' },
        sidebets: lossSidebets,
        total: -100 + lossTotal,
      };
    case 'push':
      return {
        mainBet: { label: 'Main', amount: 0, outcome: 'push' },
        sidebets,
        total: sidebetTotal,
      };
    case 'mixed':
      return {
        mainBet: { label: 'Main', amount: 25, outcome: 'win' },
        sidebets,
        total: 25 + sidebetTotal,
      };
  }
}

function hexToNumber(hex: string): number {
  return parseInt(hex.replace('#', ''), 16);
}

function formatAmount(amount: number): string {
  if (amount >= 0) return `+$${amount}`;
  return `-$${Math.abs(amount)}`;
}

export default function ScorecardDemoPage() {
  const canvasRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<PIXI.Application | null>(null);
  const scorecardRef = useRef<PIXI.Container | null>(null);

  const [config, setConfig] = useState<ScorecardConfig>(DEFAULT_CONFIG);
  const [scenario, setScenario] = useState<'win' | 'loss' | 'push' | 'mixed'>('win');
  const [sidebetCount, setSidebetCount] = useState(2);
  const [showFlipAnimation, setShowFlipAnimation] = useState(false);
  const [copied, setCopied] = useState(false);

  const getData = useCallback((): DemoData => {
    return generateScenario(scenario, sidebetCount);
  }, [scenario, sidebetCount]);

  // Initialize PixiJS
  useEffect(() => {
    if (!canvasRef.current || appRef.current) return;

    const app = new PIXI.Application();

    (async () => {
      await app.init({
        width: 400,
        height: 400,
        backgroundColor: 0x0f172a,
        antialias: true,
      });

      canvasRef.current?.appendChild(app.canvas);
      appRef.current = app;

      // Initial render
      renderScorecard(config, getData());
    })();

    return () => {
      if (appRef.current) {
        appRef.current.destroy(true);
        appRef.current = null;
      }
    };
  }, []);

  // Re-render when config or data changes
  useEffect(() => {
    if (appRef.current) {
      renderScorecard(config, getData());
    }
  }, [config, scenario, sidebetCount, getData]);

  const renderScorecard = (cfg: ScorecardConfig, data: DemoData) => {
    const app = appRef.current;
    if (!app) return;

    // Clear previous
    if (scorecardRef.current) {
      scorecardRef.current.destroy({ children: true });
    }

    const container = new PIXI.Container();
    container.position.set(200, 200); // Center in canvas

    // Use fixed dimensions
    const width = CARD_WIDTH;
    const height = CARD_HEIGHT;
    const cornerRadius = CORNER_RADIUS;

    // Background with rounded corners
    const bg = new PIXI.Graphics();
    bg.roundRect(-width / 2, -height / 2, width, height, cornerRadius);
    bg.fill(hexToNumber(cfg.backgroundColor));
    bg.stroke({ width: cfg.borderThickness, color: hexToNumber(cfg.borderColor) });
    container.addChild(bg);

    // Header background
    const headerBg = new PIXI.Graphics();
    // Top rounded corners only
    headerBg.moveTo(-width / 2 + cornerRadius, -height / 2);
    headerBg.lineTo(width / 2 - cornerRadius, -height / 2);
    headerBg.arcTo(width / 2, -height / 2, width / 2, -height / 2 + cornerRadius, cornerRadius);
    headerBg.lineTo(width / 2, -height / 2 + cfg.headerHeight);
    headerBg.lineTo(-width / 2, -height / 2 + cfg.headerHeight);
    headerBg.lineTo(-width / 2, -height / 2 + cornerRadius);
    headerBg.arcTo(-width / 2, -height / 2, -width / 2 + cornerRadius, -height / 2, cornerRadius);
    headerBg.closePath();
    headerBg.fill(hexToNumber(cfg.headerBgColor));
    container.addChild(headerBg);

    // Header text
    const headerText = new PIXI.Text({
      text: cfg.headerText,
      style: {
        fontFamily: cfg.fontFamily,
        fontSize: cfg.headerFontSize,
        fontWeight: 'bold',
        fill: hexToNumber(cfg.headerTextColor),
      }
    });
    headerText.anchor.set(0.5, 0.5);
    headerText.position.set(0, -height / 2 + cfg.headerHeight / 2);
    container.addChild(headerText);

    // Content area
    let yPos = -height / 2 + cfg.headerHeight + cfg.paddingY;

    // Render rows
    const allRows = [data.mainBet, ...data.sidebets];
    for (const row of allRows) {
      const color = row.outcome === 'win' ? cfg.winColor : row.outcome === 'loss' ? cfg.lossColor : cfg.pushColor;

      // Label
      const labelText = new PIXI.Text({
        text: row.label,
        style: {
          fontFamily: cfg.fontFamily,
          fontSize: cfg.rowFontSize,
          fill: hexToNumber(cfg.rowTextColor),
        }
      });

      // Position based on alignment
      if (cfg.labelAlign === 'left') {
        labelText.anchor.set(0, 0.5);
        labelText.position.set(-width / 2 + cfg.paddingX, yPos + cfg.rowHeight / 2);
      } else if (cfg.labelAlign === 'center') {
        labelText.anchor.set(0.5, 0.5);
        labelText.position.set(-width / 4, yPos + cfg.rowHeight / 2);
      } else {
        labelText.anchor.set(1, 0.5);
        labelText.position.set(0, yPos + cfg.rowHeight / 2);
      }
      container.addChild(labelText);

      // Value
      const valueText = new PIXI.Text({
        text: formatAmount(row.amount),
        style: {
          fontFamily: cfg.fontFamily,
          fontSize: cfg.rowFontSize,
          fontWeight: 'bold',
          fill: hexToNumber(color),
        }
      });

      if (cfg.valueAlign === 'right') {
        valueText.anchor.set(1, 0.5);
        valueText.position.set(width / 2 - cfg.paddingX, yPos + cfg.rowHeight / 2);
      } else if (cfg.valueAlign === 'center') {
        valueText.anchor.set(0.5, 0.5);
        valueText.position.set(width / 4, yPos + cfg.rowHeight / 2);
      } else {
        valueText.anchor.set(0, 0.5);
        valueText.position.set(cfg.paddingX, yPos + cfg.rowHeight / 2);
      }
      container.addChild(valueText);

      yPos += cfg.rowHeight;
    }

    // Divider line
    const divider = new PIXI.Graphics();
    divider.moveTo(-width / 2 + cfg.paddingX, yPos);
    divider.lineTo(width / 2 - cfg.paddingX, yPos);
    divider.stroke({ width: cfg.dividerThickness, color: hexToNumber(cfg.dividerColor) });
    container.addChild(divider);

    yPos += cfg.dividerThickness;

    // Total row background
    const totalBg = new PIXI.Graphics();
    // Bottom rounded corners only
    totalBg.moveTo(-width / 2, yPos);
    totalBg.lineTo(width / 2, yPos);
    totalBg.lineTo(width / 2, height / 2 - cornerRadius);
    totalBg.arcTo(width / 2, height / 2, width / 2 - cornerRadius, height / 2, cornerRadius);
    totalBg.lineTo(-width / 2 + cornerRadius, height / 2);
    totalBg.arcTo(-width / 2, height / 2, -width / 2, height / 2 - cornerRadius, cornerRadius);
    totalBg.closePath();
    totalBg.fill(hexToNumber(cfg.totalBgColor));
    container.addChild(totalBg);

    // Total label
    const totalLabel = new PIXI.Text({
      text: 'TOTAL',
      style: {
        fontFamily: cfg.fontFamily,
        fontSize: cfg.totalFontSize,
        fontWeight: 'bold',
        fill: hexToNumber(cfg.totalTextColor),
      }
    });
    totalLabel.anchor.set(0, 0.5);
    totalLabel.position.set(-width / 2 + cfg.paddingX, yPos + cfg.totalRowHeight / 2);
    container.addChild(totalLabel);

    // Total value
    const totalColor = data.total >= 0 ? cfg.winColor : cfg.lossColor;
    const totalValue = new PIXI.Text({
      text: formatAmount(data.total),
      style: {
        fontFamily: cfg.fontFamily,
        fontSize: cfg.totalFontSize,
        fontWeight: 'bold',
        fill: hexToNumber(totalColor),
      }
    });
    totalValue.anchor.set(1, 0.5);
    totalValue.position.set(width / 2 - cfg.paddingX, yPos + cfg.totalRowHeight / 2);
    container.addChild(totalValue);

    app.stage.addChild(container);
    scorecardRef.current = container;
  };

  const playFlipAnimation = () => {
    const container = scorecardRef.current;
    const app = appRef.current;
    if (!container || !app) return;

    setShowFlipAnimation(true);

    // Simple flip animation using PIXI ticker
    let phase = 'out'; // 'out' then 'in'
    const duration = 200; // ms per phase
    let elapsed = 0;

    const animate = (ticker: PIXI.Ticker) => {
      elapsed += ticker.deltaMS;
      const progress = Math.min(elapsed / duration, 1);

      if (phase === 'out') {
        // Scale X from 1 to 0
        container.scale.x = 1 - progress;
        if (progress >= 1) {
          phase = 'in';
          elapsed = 0;
        }
      } else {
        // Scale X from 0 to 1
        container.scale.x = progress;
        if (progress >= 1) {
          app.ticker.remove(animate);
          setShowFlipAnimation(false);
        }
      }
    };

    app.ticker.add(animate);
  };

  const copyConfig = () => {
    const fullConfig = {
      // Fixed dimensions (for reference)
      _fixedDimensions: { width: CARD_WIDTH, height: CARD_HEIGHT, cornerRadius: CORNER_RADIUS },
      ...config
    };
    const configJson = JSON.stringify(fullConfig, null, 2);
    navigator.clipboard.writeText(configJson);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const resetConfig = () => {
    setConfig(DEFAULT_CONFIG);
  };

  const updateConfig = <K extends keyof ScorecardConfig>(key: K, value: ScorecardConfig[K]) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  // Control components
  const NumberInput = ({ label, field, min, max, step = 1 }: { label: string; field: keyof ScorecardConfig; min: number; max: number; step?: number }) => (
    <div className="flex items-center gap-2 mb-2">
      <label className="w-32 text-sm text-gray-300">{label}</label>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={config[field] as number}
        onChange={e => updateConfig(field, Number(e.target.value))}
        className="flex-1"
      />
      <input
        type="number"
        value={config[field] as number}
        onChange={e => updateConfig(field, Number(e.target.value))}
        className="w-16 px-2 py-1 bg-gray-700 text-white rounded text-sm"
      />
    </div>
  );

  const ColorInput = ({ label, field }: { label: string; field: keyof ScorecardConfig }) => (
    <div className="flex items-center gap-2 mb-2">
      <label className="w-32 text-sm text-gray-300">{label}</label>
      <input
        type="color"
        value={config[field] as string}
        onChange={e => updateConfig(field, e.target.value)}
        className="w-10 h-8 rounded cursor-pointer"
      />
      <input
        type="text"
        value={config[field] as string}
        onChange={e => updateConfig(field, e.target.value)}
        className="flex-1 px-2 py-1 bg-gray-700 text-white rounded text-sm font-mono"
      />
    </div>
  );

  const TextInput = ({ label, field }: { label: string; field: keyof ScorecardConfig }) => (
    <div className="flex items-center gap-2 mb-2">
      <label className="w-32 text-sm text-gray-300">{label}</label>
      <input
        type="text"
        value={config[field] as string}
        onChange={e => updateConfig(field, e.target.value)}
        className="flex-1 px-2 py-1 bg-gray-700 text-white rounded text-sm"
      />
    </div>
  );

  const SelectInput = ({ label, field, options }: { label: string; field: keyof ScorecardConfig; options: string[] }) => (
    <div className="flex items-center gap-2 mb-2">
      <label className="w-32 text-sm text-gray-300">{label}</label>
      <select
        value={config[field] as string}
        onChange={e => updateConfig(field, e.target.value)}
        className="flex-1 px-2 py-1 bg-gray-700 text-white rounded text-sm"
      >
        {options.map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-900 text-white p-4">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl font-bold mb-4">Scorecard Styling Demo <span className="text-sm font-normal text-gray-400">(Fixed: {CARD_WIDTH}×{CARD_HEIGHT}px)</span></h1>

        <div className="flex gap-6">
          {/* Canvas Preview */}
          <div className="flex-shrink-0">
            <div className="bg-slate-800 rounded-lg p-4">
              <div ref={canvasRef} className="rounded overflow-hidden" />

              {/* Scenario toggles */}
              <div className="mt-4">
                <div className="text-sm text-gray-400 mb-2">Main Bet Outcome:</div>
                <div className="flex gap-2">
                  {(['win', 'loss', 'push', 'mixed'] as const).map(s => (
                    <button
                      key={s}
                      onClick={() => setScenario(s)}
                      className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                        scenario === s
                          ? 'bg-yellow-500 text-black'
                          : 'bg-gray-700 text-white hover:bg-gray-600'
                      }`}
                    >
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sidebet count */}
              <div className="mt-3">
                <div className="text-sm text-gray-400 mb-2">Sidebets Displayed:</div>
                <div className="flex gap-2">
                  {[0, 1, 2, 3].map(count => (
                    <button
                      key={count}
                      onClick={() => setSidebetCount(count)}
                      className={`px-4 py-1 rounded text-sm font-medium transition-colors ${
                        sidebetCount === count
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-700 text-white hover:bg-gray-600'
                      }`}
                    >
                      {count}
                    </button>
                  ))}
                </div>
              </div>

              {/* Animation test */}
              <button
                onClick={playFlipAnimation}
                disabled={showFlipAnimation}
                className="mt-3 w-full px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 rounded font-medium transition-colors"
              >
                {showFlipAnimation ? 'Playing...' : 'Test Flip Animation'}
              </button>
            </div>
          </div>

          {/* Controls */}
          <div className="flex-1 bg-slate-800 rounded-lg p-4 overflow-y-auto max-h-[80vh]">
            {/* Action buttons */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={copyConfig}
                className={`px-4 py-2 rounded font-medium transition-colors ${
                  copied
                    ? 'bg-green-600 text-white'
                    : 'bg-yellow-500 text-black hover:bg-yellow-400'
                }`}
              >
                {copied ? 'Copied!' : 'Copy Config'}
              </button>
              <button
                onClick={resetConfig}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-500 rounded font-medium transition-colors"
              >
                Reset to Default
              </button>
            </div>

            {/* Fixed dimensions info */}
            <div className="mb-6 p-3 bg-gray-700 rounded">
              <div className="text-sm text-gray-300">
                <span className="text-yellow-400 font-semibold">Fixed Dimensions:</span> {CARD_WIDTH}w × {CARD_HEIGHT}h (corner radius: {CORNER_RADIUS})
              </div>
            </div>

            {/* Colors */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 text-yellow-400">Colors</h3>
              <ColorInput label="Background" field="backgroundColor" />
              <ColorInput label="Border" field="borderColor" />
              <ColorInput label="Header BG" field="headerBgColor" />
              <ColorInput label="Header Text" field="headerTextColor" />
              <ColorInput label="Divider" field="dividerColor" />
              <ColorInput label="Row Text" field="rowTextColor" />
              <ColorInput label="Win Color" field="winColor" />
              <ColorInput label="Loss Color" field="lossColor" />
              <ColorInput label="Push Color" field="pushColor" />
              <ColorInput label="Total BG" field="totalBgColor" />
              <ColorInput label="Total Text" field="totalTextColor" />
            </div>

            {/* Typography */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 text-yellow-400">Typography</h3>
              <TextInput label="Header Text" field="headerText" />
              <SelectInput label="Font Family" field="fontFamily" options={['Arial', 'Verdana', 'Georgia', 'Times New Roman', 'Courier New', 'Impact']} />
              <NumberInput label="Header Font" field="headerFontSize" min={6} max={16} />
              <NumberInput label="Row Font" field="rowFontSize" min={5} max={12} />
              <NumberInput label="Total Font" field="totalFontSize" min={6} max={14} />
            </div>

            {/* Spacing */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 text-yellow-400">Spacing</h3>
              <NumberInput label="Border Width" field="borderThickness" min={0} max={4} />
              <NumberInput label="Header Height" field="headerHeight" min={10} max={30} />
              <NumberInput label="Row Height" field="rowHeight" min={8} max={20} />
              <NumberInput label="Total Row Height" field="totalRowHeight" min={10} max={30} />
              <NumberInput label="Padding X" field="paddingX" min={2} max={16} />
              <NumberInput label="Padding Y" field="paddingY" min={1} max={10} />
              <NumberInput label="Divider Width" field="dividerThickness" min={0} max={3} />
            </div>

            {/* Alignment */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 text-yellow-400">Alignment</h3>
              <SelectInput label="Label Align" field="labelAlign" options={['left', 'center', 'right']} />
              <SelectInput label="Value Align" field="valueAlign" options={['left', 'center', 'right']} />
            </div>
          </div>
        </div>

        {/* Config Output */}
        <div className="mt-6 bg-slate-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold mb-3 text-yellow-400">Current Config (JSON)</h3>
          <pre className="bg-gray-900 p-4 rounded text-sm overflow-x-auto text-green-400 font-mono">
            {JSON.stringify({
              // Fixed dimensions (for reference)
              _fixedDimensions: { width: CARD_WIDTH, height: CARD_HEIGHT, cornerRadius: CORNER_RADIUS },
              ...config
            }, null, 2)}
          </pre>
        </div>
      </div>
    </div>
  );
}
